import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const VisionMission = () => {
  const { t } = useTranslation();

  return (
    <div className="pt-16 md:pt-20">
      {/* Military Hero Section */}
      <section className="section-military bg-black">
        <div className="container-military text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-black tracking-tighter mb-8 text-gradient-military"
          >
            {t('vision.page.main.title')}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-32 h-1 bg-gradient-to-r from-military-accent to-military-accent mx-auto"
          />
        </div>
      </section>

      {/* Main Content */}
      <section className="section-military bg-gray-900">
        <div className="container-military">
          <div className="max-w-4xl mx-auto">
            {/* Vision Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-gradient-military mb-8">
                {t('vision.page.vision.title')}
              </h2>
              
              <p className="text-lg md:text-xl text-military-silver leading-relaxed mb-6 font-military-mono">
                {t('vision.page.vision.description1')}
              </p>
              
              <p className="text-lg md:text-xl text-military-silver leading-relaxed mb-8 font-military-mono">
                {t('vision.page.vision.description2')}
              </p>
            </motion.div>

            {/* Mission Statement */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="military-panel p-8 md:p-12 mb-16"
            >
              <h3 className="text-2xl md:text-3xl font-black tracking-tighter text-gradient-military mb-6">
                {t('vision.page.mission.title')}
              </h3>
              <p className="text-lg md:text-xl text-military-silver leading-relaxed font-military-mono">
                {t('vision.page.mission.description')}
              </p>
            </motion.div>

            {/* Core Values Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
            >
              <div className="military-card text-center p-6 md:p-8">
                <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h4 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">{t('vision.page.values.innovation.title')}</h4>
                <p className="text-military-silver font-military-mono text-sm md:text-base">
                  {t('vision.page.values.innovation.description')}
                </p>
              </div>

              <div className="military-card text-center p-6 md:p-8">
                <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">{t('vision.page.values.excellence.title')}</h4>
                <p className="text-military-silver font-military-mono text-sm md:text-base">
                  {t('vision.page.values.excellence.description')}
                </p>
              </div>

              <div className="military-card text-center p-6 md:p-8">
                <div className="w-16 h-16 bg-military-accent rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h4 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-4">{t('vision.page.values.integrity.title')}</h4>
                <p className="text-military-silver font-military-mono text-sm md:text-base">
                  {t('vision.page.values.integrity.description')}
                </p>
              </div>
            </motion.div>

            {/* Strategic Goals */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="mt-16"
            >
              <h3 className="text-2xl md:text-3xl font-black tracking-tighter text-gradient-military mb-8 text-center">
                {t('vision.page.goals.title')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div className="military-card p-6 md:p-8">
                  <h4 className="text-xl font-black tracking-tighter text-gradient-military mb-4">{t('vision.page.goals.technology.title')}</h4>
                  <p className="text-military-silver font-military-mono text-sm md:text-base">
                    {t('vision.page.goals.technology.description')}
                  </p>
                </div>
                <div className="military-card p-6 md:p-8">
                  <h4 className="text-xl font-black tracking-tighter text-gradient-military mb-4">{t('vision.page.goals.expansion.title')}</h4>
                  <p className="text-military-silver font-military-mono text-sm md:text-base">
                    {t('vision.page.goals.expansion.description')}
                  </p>
                </div>
                <div className="military-card p-6 md:p-8">
                  <h4 className="text-xl font-black tracking-tighter text-gradient-military mb-4">{t('vision.page.goals.growth.title')}</h4>
                  <p className="text-military-silver font-military-mono text-sm md:text-base">
                    {t('vision.page.goals.growth.description')}
                  </p>
                </div>
                <div className="military-card p-6 md:p-8">
                  <h4 className="text-xl font-black tracking-tighter text-gradient-military mb-4">{t('vision.page.goals.talent.title')}</h4>
                  <p className="text-military-silver font-military-mono text-sm md:text-base">
                    {t('vision.page.goals.talent.description')}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisionMission; 